import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';

/**
 * Browser push subscription lifecycle: register the service worker, swap the VAPID key for a
 * subscription, and tell BC_Corporate about it.
 *
 * Registers a hand-written push-only worker rather than @angular/service-worker — see push-sw.js
 * for why. Nothing here caches or intercepts requests.
 */
@Injectable({ providedIn: 'root' })
export class WebPushService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiConfig.corporateAPI.endpoint}/notifications/push`;

  private registration?: ServiceWorkerRegistration;

  /** False on browsers without push, and on iOS Safari unless the PWA is installed to the home screen. */
  get isSupported(): boolean {
    return typeof navigator !== 'undefined'
      && 'serviceWorker' in navigator
      && typeof window !== 'undefined'
      && 'PushManager' in window;
  }

  /** The endpoint this browser currently holds, or null when it has none. */
  async currentEndpoint(): Promise<string | null> {
    if (!this.isSupported) return null;

    const registration = await this.ensureRegistered();
    const subscription = await registration?.pushManager.getSubscription();

    return subscription?.endpoint ?? null;
  }

  /**
   * Subscribes this browser and records it server-side. Returns false when push is unavailable or
   * the server has no VAPID key configured, so the caller can leave the toggle off rather than
   * pretend it worked.
   */
  async subscribe(): Promise<boolean> {
    if (!this.isSupported) return false;

    const publicKey = await this.fetchPublicKey();
    if (!publicKey) return false;

    const registration = await this.ensureRegistered();
    if (!registration) return false;

    try {
      // Reuse an existing subscription when there is one: calling subscribe() again with the same
      // key returns the same endpoint, but going through getSubscription() first avoids a needless
      // round trip to the push service.
      const subscription = await registration.pushManager.getSubscription()
        ?? await registration.pushManager.subscribe({
          // Required by Chrome: it refuses subscriptions that may deliver silently.
          userVisibleOnly: true,
          applicationServerKey: WebPushService.urlBase64ToUint8Array(publicKey),
        });

      await firstValueFrom(this.http.post(`${this.baseUrl}/subscribe`, this.toDto(subscription)));
      return true;
    } catch {
      // Permission denied, or the push service refused. Nothing to recover — report failure.
      return false;
    }
  }

  /**
   * Registers this browser when the user already has push switched on but this device is not
   * subscribed — a browser they have not used before, or one whose subscription the browser rotated
   * or expired (which happens silently and is a common cause of "push just stopped").
   *
   * Only acts when permission is ALREADY granted. Notification permission is per-browser and can
   * only be requested from a user gesture, so a device that has never granted it cannot be enrolled
   * from here no matter what the saved preference says — the settings page prompts for that.
   *
   * Idempotent: subscribe() reuses any existing subscription and the server upserts by endpoint, so
   * this both creates a missing registration and repairs one the server lost.
   */
  async ensureSubscribed(): Promise<boolean> {
    if (!this.isSupported) return false;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;

    return this.subscribe();
  }

  /** Removes this browser's subscription locally and server-side. Safe to call when not subscribed. */
  async unsubscribe(): Promise<void> {
    if (!this.isSupported) return;

    const registration = await this.ensureRegistered();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;

    const dto = this.toDto(subscription);

    try {
      // Tell the server first: if the local unsubscribe succeeded but this failed, the row would
      // linger and keep receiving pushes this browser can no longer show.
      await firstValueFrom(this.http.post(`${this.baseUrl}/unsubscribe`, dto));
    } catch {
      // Best effort — the send path prunes endpoints the push service reports as gone anyway.
    }

    try {
      await subscription.unsubscribe();
    } catch {
      // Already gone.
    }
  }

  private async fetchPublicKey(): Promise<string | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ publicKey: string }>(`${this.baseUrl}/public-key`)
      );
      return response?.publicKey || null;
    } catch {
      // 503 means push is not configured for this environment.
      return null;
    }
  }

  private async ensureRegistered(): Promise<ServiceWorkerRegistration | undefined> {
    if (this.registration) return this.registration;

    try {
      // Absolute path. A relative 'push-sw.js' resolves against the CURRENT PAGE, so registering
      // from /settings/notifications requested /settings/push-sw.js — which the dev server answers
      // with index.html instead of 404 thanks to its SPA history fallback. That registers a worker
      // against the wrong script URL, and update() then re-fetches that same wrong URL forever, so
      // edits to this file never reach the browser.
      this.registration = await navigator.serviceWorker.register('/push-sw.js', {
        scope: '/',
        // Never answer the worker script from the HTTP cache. Without this a changed push-sw.js can
        // keep serving the old one for up to 24h, which is how an edit to the notification icon
        // shipped but never reached browsers that had already registered.
        updateViaCache: 'none',
      });

      // Force an update check on every app load. register() alone reuses an existing registration
      // without necessarily re-fetching the script, so a fix inside the worker would otherwise only
      // land once the browser happened to check.
      void this.registration.update().catch(() => undefined);

      await navigator.serviceWorker.ready;
      return this.registration;
    } catch {
      return undefined;
    }
  }

  private toDto(subscription: PushSubscription) {
    const json = subscription.toJSON();

    return {
      endpoint: subscription.endpoint,
      p256dh: json.keys?.['p256dh'] ?? '',
      auth: json.keys?.['auth'] ?? '',
      userAgent: navigator.userAgent,
    };
  }

  /**
   * The VAPID key is base64url text but PushManager wants raw bytes.
   *
   * Returns the ArrayBuffer rather than the Uint8Array view: applicationServerKey is typed as
   * BufferSource, and under newer TypeScript lib definitions a Uint8Array is generic over its
   * backing buffer (which may be a SharedArrayBuffer) and no longer assignable to it.
   */
  private static urlBase64ToUint8Array(base64String: string): ArrayBuffer {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);

    const buffer = new ArrayBuffer(raw.length);
    const output = new Uint8Array(buffer);
    for (let i = 0; i < raw.length; i++) {
      output[i] = raw.charCodeAt(i);
    }
    return buffer;
  }
}
